import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "./Dashboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MailPlus, Send } from "lucide-react";

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ booking_id: "", amount: "", deposit_paid: "", due_date: "", notes: "" });

  async function load() {
    const [inv, bk] = await Promise.all([api.get("/admin/invoices"), api.get("/admin/bookings?status_filter=all")]);
    setInvoices(inv.data);
    setBookings(bk.data);
  }
  useEffect(() => { load(); }, []);

  async function create() {
    if (!draft.booking_id) return toast.error("Select a booking");
    try {
      const payload = {
        booking_id: draft.booking_id,
        amount: draft.amount ? Number(draft.amount) : undefined,
        deposit_paid: draft.deposit_paid ? Number(draft.deposit_paid) : 0,
        due_date: draft.due_date || undefined,
        notes: draft.notes,
      };
      await api.post("/admin/invoices", payload);
      setOpen(false);
      setDraft({ booking_id: "", amount: "", deposit_paid: "", due_date: "", notes: "" });
      toast.success("Invoice created");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function send(id) {
    try {
      await api.post(`/admin/invoices/${id}/send`);
      toast.success("Invoice emailed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function setStatus(id, status) {
    try {
      await api.patch(`/admin/invoices/${id}?status_value=${status}`);
      toast.success("Invoice updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-8" data-testid="admin-invoices-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow mb-2">Invoices</div>
          <h1 className="font-serif text-4xl font-light">Billing & invoices</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="inline-flex items-center gap-2 px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90" data-testid="open-create-invoice">
              <MailPlus className="w-4 h-4" /> New invoice
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-2xl font-light">Create invoice</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <label className="block">
                <span className="eyebrow">Booking</span>
                <Select value={draft.booking_id} onValueChange={(v) => setDraft({ ...draft, booking_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="invoice-booking-select"><SelectValue placeholder="Select a booking" /></SelectTrigger>
                  <SelectContent>
                    {bookings.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.reference} · {b.customer_name} · {b.service_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Amount (USD)" testid="invoice-amount" value={draft.amount} onChange={(v) => setDraft({ ...draft, amount: v })} type="number" />
                <Input label="Deposit paid" testid="invoice-deposit-paid" value={draft.deposit_paid} onChange={(v) => setDraft({ ...draft, deposit_paid: v })} type="number" />
              </div>
              <Input label="Due date (YYYY-MM-DD)" testid="invoice-due" value={draft.due_date} onChange={(v) => setDraft({ ...draft, due_date: v })} />
              <label className="block">
                <span className="eyebrow">Notes</span>
                <textarea rows={3} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="mt-1 w-full bg-transparent border border-border p-2 outline-none focus:border-foreground" data-testid="invoice-notes" />
              </label>
            </div>
            <DialogFooter>
              <button onClick={create} className="inline-flex items-center gap-2 px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em]" data-testid="invoice-create-btn">Create</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="px-5 py-3 eyebrow font-normal">Invoice</th>
              <th className="px-5 py-3 eyebrow font-normal">Client</th>
              <th className="px-5 py-3 eyebrow font-normal">Service</th>
              <th className="px-5 py-3 eyebrow font-normal">Amount</th>
              <th className="px-5 py-3 eyebrow font-normal">Balance</th>
              <th className="px-5 py-3 eyebrow font-normal">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (<tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">No invoices yet. Create one from a booking.</td></tr>)}
            {invoices.map((i) => (
              <tr key={i.id} className="border-t border-border">
                <td className="px-5 py-4 font-mono text-xs">{i.invoice_number}</td>
                <td className="px-5 py-4">
                  <div>{i.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{i.customer_email}</div>
                </td>
                <td className="px-5 py-4">{i.service_name} · {i.tier_name}</td>
                <td className="px-5 py-4">${i.amount.toFixed(0)}</td>
                <td className="px-5 py-4">${i.balance_due.toFixed(0)}</td>
                <td className="px-5 py-4"><StatusBadge value={i.status} /></td>
                <td className="px-5 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => send(i.id)} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-border hover:border-foreground/60" data-testid={`send-invoice-${i.invoice_number}`}>
                      <Send className="w-3.5 h-3.5" /> Email
                    </button>
                    {i.status !== "paid" && (
                      <button onClick={() => setStatus(i.id, "paid")} className="inline-flex items-center px-3 h-8 text-xs bg-foreground text-background" data-testid={`mark-paid-${i.invoice_number}`}>
                        Mark paid
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <input type={type} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm" />
    </label>
  );
}
